import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';

import { Sidebar } from './Sidebar';
import { connectLocation } from '../../../../location/LocationConnectors';
import { useTRC } from '../../store';
import generateResultsPdf from '../WallsAboveGrade/pdfResults';
import CalculatorLayout from '../shared/CalculatorLayout';
import Footer from '../shared/Footer';
import ResultsBlock from '../shared/ResultsBlock';
import {
  AIR_FILM_EXTERIOR_RSI,
  WOOD_STRAP_BEHIND_GWB_RSI,
  INTERIOR_GWB_RSI_PER_MM,
  validateInputs,
  getFramingText,
  resetResults,
  insulationOptions,
} from '../shared/constants';
import { smoothScrollIntoView } from '../shared/scroll';

const rsiRange = {
  '2x4': { values: [9.3, 12, 14] },
  '2x6': { values: [14.6, 19, 22, 24] },
};

export const toRSI = {
  9.3: 1.56,
  12: 2.11,
  14: 2.46,
  14.6: 2.66,
  19: 3.34,
  22: 3.87,
  24: 4.23,
};

const rsiOptions = insulationOptions(rsiRange, toRSI);

const ROOF_RSIF = { '2x4': 0.757, '2x6': 1.19 };
const AIR_FILM_INTERIOR_RSI = 0.11;

// Fractions of area at the frame for each spacing (from R1-1 “Frame Cavity Percentage”)
const AF_BY_SPACING_ROOF = { 304: 0.155, 406: 0.14, 488: 0.125, 610: 0.11 };

const getOnCenterSpacingOptions = t => ({
  304: t('thermal_calculator.dimensions.spacing_304'),
  406: t('thermal_calculator.dimensions.spacing_406'),
  488: t('thermal_calculator.dimensions.spacing_488'),
  610: t('thermal_calculator.dimensions.spacing_610'),
});

// —— defaults for this calc’s inputs
const DEFAULTS = {
  framingType: '2x4',
  onCenterSpacing: '304',
  cavityRSI: '',
  gypsumBoardThickness: '12.7',
  hasWoodStrapping: true,
  hasVapourBarrier: true,
  aboveTrussRSI: '0',
  aboveTrussUnit: '',
};

const RoofBelowAttics = ({ calcId, t, locale }) => {
  const { code } = locale || {};

  // ---- read location from the store
  const { state, setResults: publishResults, setInputs: publishInputs } = useTRC();
  const { province, region, hdd } = state.location;
  const hasLocation = !!(province && region);
  const onCenterSpacingOptions = getOnCenterSpacingOptions(t);

  // —— hydrate local state from store (if present), else use defaults
  const savedInputs = state.inputsByCalc?.[calcId];
  const [framingType, setFramingType] = useState(savedInputs?.framingType ?? DEFAULTS.framingType);
  const [onCenterSpacing, setOnCenterSpacing] = useState(savedInputs?.onCenterSpacing ?? DEFAULTS.onCenterSpacing);
  const [cavityRSI, setCavityRSI] = useState(savedInputs?.cavityRSI ?? DEFAULTS.cavityRSI);
  const [gypsumBoardThickness, setGypsumBoardThickness] = useState(
    savedInputs?.gypsumBoardThickness ?? DEFAULTS.gypsumBoardThickness
  );
  const [hasWoodStrapping, setHasWoodStrapping] = useState(savedInputs?.hasWoodStrapping ?? DEFAULTS.hasWoodStrapping);
  const [hasVapourBarrier, setHasVapourBarrier] = useState(savedInputs?.hasVapourBarrier ?? DEFAULTS.hasVapourBarrier);
  const [aboveTrussRSI, setAboveTrussRSI] = useState(savedInputs?.aboveTrussRSI ?? DEFAULTS.aboveTrussRSI);
  const [aboveTrussUnit, setAboveTrussUnit] = useState(savedInputs?.aboveTrussUnit ?? DEFAULTS.aboveTrussUnit);

  const resultsRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!error) return;
    const timeout = setTimeout(() => setError(null), 5000);
    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timeout);
  }, [error]);

  // —— whenever inputs change, publish them to the store so tab switches persist
  useEffect(() => {
    publishInputs({
      calcId,
      inputs: {
        framingType,
        onCenterSpacing,
        cavityRSI,
        gypsumBoardThickness,
        hasWoodStrapping,
        hasVapourBarrier,
        aboveTrussRSI,
        aboveTrussUnit,
      },
    });
  }, [
    calcId,
    framingType,
    onCenterSpacing,
    cavityRSI,
    gypsumBoardThickness,
    hasWoodStrapping,
    hasVapourBarrier,
    aboveTrussRSI,
    aboveTrussUnit,
    publishInputs,
  ]);

  const results = state.resultsByCalc?.[calcId]?.results || null;

  const rowsForTable = [
    { label: t('thermal_calculator.effective_assembly_rsi'), rsi: 'effectiveAssemblyRSI' },
    { label: t('thermal_calculator.center_of_cavity'), rsi: 'centerOfCavityRSI' },
    { label: t('thermal_calculator.installed_insulation_rsi'), rsi: 'installedInsulationRSI' },
  ];

  // ---- calculator
  const calculate = () => {
    const Af = AF_BY_SPACING_ROOF[onCenterSpacing] ?? 0.14; // framing fraction
    const Ac = Math.max(0, 1 - Af); // cavity fraction
    const RSIf = ROOF_RSIF[framingType] ?? 0; // frame path RSI (wood)
    const RSIc = parseFloat(cavityRSI || '0'); // nominal cavity RSI

    // continuous pieces (numbers, not strings)
    const gwbT = parseFloat(gypsumBoardThickness || '0');
    const interiorGwbRSI = INTERIOR_GWB_RSI_PER_MM * gwbT;
    const strapBehindGwbRSI = hasWoodStrapping ? WOOD_STRAP_BEHIND_GWB_RSI : 0;
    const vapourBarrierRSI = hasVapourBarrier ? 0 : 0; // explicit zero
    const aboveTrussValue = parseFloat(aboveTrussRSI || '0');
    const aboveRSI = aboveTrussUnit === 'rsi' ? aboveTrussValue : aboveTrussValue / 5.678 || 0;

    // exterior air film in vented space & roof sheathing are excluded per footnotes -> 0
    const roofSheathingRSI = 0;
    const extFilmInVentedRSI = AIR_FILM_EXTERIOR_RSI;

    // total continuous that participates in the parallel sum result we report
    const continuousTotalRSI =
      interiorGwbRSI + AIR_FILM_EXTERIOR_RSI + strapBehindGwbRSI + vapourBarrierRSI + AIR_FILM_INTERIOR_RSI + aboveRSI;

    // parallel path across frame / cavity
    const RSIparallel = RSIf > 0 && RSIc > 0 ? 1 / (Af / RSIf + Ac / RSIc) : 0;

    // It rounds to 2 decimal places only at the end
    const effectiveAssemblyRSI = Math.round(RSIparallel * 100) / 100 + continuousTotalRSI;
    const centerOfCavityRSI = RSIc + continuousTotalRSI;
    const installedInsulationRSI = RSIc + aboveRSI;

    // ---------- Normalized layer ledger for the PDF ----------
    const spacingText = onCenterSpacingOptions[onCenterSpacing];
    const nominalRsiText = rsiOptions[framingType].find(opt => String(opt.value) === String(cavityRSI))?.text || '';

    const framingText = getFramingText({ t });

    const layers = {
      continuous: [
        {
          id: 'roof_sheathing',
          label: `${t('thermal_calculator.roofs_below_attics.roof_sheathing')}*`,
          rsi: roofSheathingRSI,
        },
        {
          id: 'ext_air_vented',
          label: `${t('thermal_calculator.roofs_below_attics.exterior_air_film')}²`,
          rsi: extFilmInVentedRSI,
        },
        ...(aboveRSI > 0
          ? [{ id: 'above_truss', label: t('thermal_calculator.roofs_below_attics.above_roof_truss'), rsi: aboveRSI }]
          : []),
        { id: 'vb', label: t('thermal_calculator.roofs_below_attics.vapour_retarder'), rsi: vapourBarrierRSI },
        ...(hasWoodStrapping
          ? [
              {
                id: 'strap_air_up',
                label: t('thermal_calculator.roofs_below_attics.strap_air_up'),
                rsi: strapBehindGwbRSI,
              },
            ]
          : []),
        {
          id: 'gwb',
          label: `${t('thermal_calculator.floors_over_unheated_spaces.gypsum_board')} ${new Intl.NumberFormat(
            code
          ).format(gwbT)} mm (1/2${t('thermal_calculator.inches')})`,
          rsi: interiorGwbRSI,
        },
        {
          id: 'int_air_up',
          label: t('thermal_calculator.roofs_below_attics.interior_air_film'),
          rsi: AIR_FILM_INTERIOR_RSI,
        },
      ],
      nonContinuous: [
        {
          id: 'bottom_chord',
          // eslint-disable-next-line max-len
          label: `${t('thermal_calculator.roofs_below_attics.roof_truss')} (${
            framingText[framingType]
          }), ${spacingText} ${t('thermal_calculator.floors_over_unheated_spaces.on_center')}, ${nominalRsiText}, ${t(
            'thermal_calculator.roofs_below_attics.nominal_insulation'
          )}`,
          rsi: RSIparallel,
        },
      ],
    };

    return {
      Af,
      Ac,
      RSIf,
      RSIc,
      RSIparallel,
      interiorGwbRSI,
      strapBehindGwbRSI,
      continuousTotalRSI,
      effectiveAssemblyRSI,
      centerOfCavityRSI,
      installedInsulationRSI,
      layers,
    };
  };

  const onCalculate = () => {
    if (!validateInputs({ province, region, setError, cavityRSI, t })) {
      smoothScrollIntoView(resultsRef, { block: 'start', behavior: 'smooth', offset: 0 });
      // do not change persisted inputs; just report error
      return;
    }
    // inputs are valid, clear any previous error
    setError(null);

    // perform calculation
    const res = calculate();
    publishResults({
      calcId,
      results: res,
      meta: { title: t('thermal_calculator.titles.roofs_below_attics') },
    });
    smoothScrollIntoView(resultsRef, { block: 'start', behavior: 'smooth', offset: 0 });
  };

  const onReset = () => {
    resetResults(calcId, t('thermal_calculator.titles.roofs_below_attics'), publishResults);
    // reset local inputs to defaults
    setFramingType(DEFAULTS.framingType);
    setOnCenterSpacing(DEFAULTS.onCenterSpacing);
    setGypsumBoardThickness(DEFAULTS.gypsumBoardThickness);
    setHasWoodStrapping(DEFAULTS.hasWoodStrapping);
    setHasVapourBarrier(DEFAULTS.hasVapourBarrier);
    setCavityRSI(DEFAULTS.cavityRSI);
    setAboveTrussRSI(DEFAULTS.aboveTrussRSI);
    setAboveTrussUnit(DEFAULTS.aboveTrussUnit);
    setError(null);
    // clear inputs in store
    publishInputs({ calcId, inputs: null });
  };

  const sidebar = (
    <Sidebar
      calcId={calcId}
      insulationOptions={rsiOptions[framingType] || []}
      framingType={framingType}
      setFramingType={setFramingType}
      cavityRSI={cavityRSI}
      setCavityRSI={setCavityRSI}
      onCenterSpacing={onCenterSpacing}
      setOnCenterSpacing={setOnCenterSpacing}
      onCenterSpacingOptions={onCenterSpacingOptions}
      gypsumBoardThickness={gypsumBoardThickness}
      setGypsumBoardThickness={setGypsumBoardThickness}
      hasWoodStrapping={hasWoodStrapping}
      setHasWoodStrapping={setHasWoodStrapping}
      hasVapourBarrier={hasVapourBarrier}
      setHasVapourBarrier={setHasVapourBarrier}
      aboveTrussRSI={aboveTrussRSI}
      setAboveTrussRSI={setAboveTrussRSI}
      aboveTrussUnit={aboveTrussUnit}
      setAboveTrussUnit={setAboveTrussUnit}
      onCalculate={onCalculate}
      onReset={onReset}
      hasLocation={hasLocation}
      t={t}
    />
  );

  const handleDownload = () => {
    if (!results) return;
    generateResultsPdf({
      calcId,
      province,
      region,
      hdd,
      projectLink: 'https://insulation.owenscorning.ca/builders/calculators/thermal-project-calculator/',
      assemblyHeading: t('thermal_calculator.titles.roofs_below_attics'),
      tableTitle: t('thermal_calculator.entire_assembly'),
      tableSubtitle: t('thermal_calculator.effective_thermal_resistance'),
      rowsConfig: rowsForTable,
      results,
      t,
    });
  };

  const resultsPane = (
    <div ref={resultsRef}>
      <ResultsBlock
        calcId={calcId}
        province={province}
        region={region}
        hdd={hdd}
        error={error}
        results={results}
        rows={rowsForTable}
      />
      <Footer results={results} onDownload={handleDownload} />
    </div>
  );

  return <CalculatorLayout sidebar={sidebar} results={resultsPane} />;
};

RoofBelowAttics.propTypes = {
  // supplied by ThermalResistanceCalculator when rendering each calc
  calcId: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  locale: PropTypes.shape({
    code: PropTypes.string.isRequired,
  }).isRequired,
};

export default connectLocation(RoofBelowAttics);
